'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import dynamic from 'next/dynamic';
import {
  CategoryIcon, IconCheck, IconSend, IconLock, IconMail, IconExternal,
  IconArrowRight, IconArrowLeft, IconShield,
} from '../_components/icons';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Category { id: string; name: string; icon: string | null; color: string; description_hint: string | null; }
interface WizardState {
  categoryId: string; categoryName: string; categoryColor: string;
  lat: number | null; lng: number | null; addressText: string;
  description: string; files: File[];
  contactEmail: string; notifyOnUpdate: boolean;
}
type Step = 1 | 2 | 3 | 4 | 5;

// ─── Step Indicator ───────────────────────────────────────────────────────────

const STEP_LABELS = ['Kategoria', 'Lokalizacja', 'Opis', 'Kontakt', 'Podsumowanie'];

function StepIndicator({ current }: { current: Step }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8 overflow-x-auto py-2">
      {STEP_LABELS.map((label, i) => {
        const step = (i + 1) as Step;
        const done = step < current;
        const active = step === current;
        return (
          <div key={step} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5 min-w-[62px]">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                done ? 'bg-emerald-500 text-white' : active ? 'bg-gradient-to-br from-sky-500 to-indigo-500 text-white shadow-md shadow-sky-500/30 scale-110' : 'bg-[#F2F4F7] text-[#98A2B3]'
              }`}>
                {done ? <IconCheck className="w-4 h-4" /> : step}
              </div>
              <span className={`text-xs font-medium hidden sm:block transition-colors ${active ? 'text-sky-600' : done ? 'text-emerald-600' : 'text-[#98A2B3]'}`}>
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className={`h-0.5 w-8 sm:w-12 mx-1 transition-colors duration-300 ${done ? 'bg-emerald-400' : 'bg-[#E4E7EC]'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Map component (dynamic — no SSR) ────────────────────────────────────────

const MapPicker = dynamic(() => import('./MapPicker'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-[320px] bg-[#F2F4F7] rounded-xl flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#0EA5E9] border-t-transparent rounded-full animate-spin" />
    </div>
  ),
});

// ─── Helper: file size ────────────────────────────────────────────────────────

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

export default function ReportPage() {
  const [step, setStep] = useState<Step>(1);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  const [error, setError] = useState('');
  const [isExternalManager, setIsExternalManager] = useState(false);
  const [externalManagerName, setExternalManagerName] = useState<string | null>(null);
  const [roadClassLabel, setRoadClassLabel] = useState<string | null>(null);
  const [notificationSent, setNotificationSent] = useState(false);

  // Stały klucz idempotencji na czas wypełniania formularza — chroni przed
  // utworzeniem duplikatu przy podwójnym kliknięciu / ponowieniu po błędzie sieci.
  const idempotencyKey = useRef<string>(
    (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`),
  );

  const [state, setState] = useState<WizardState>({
    categoryId: '', categoryName: '', categoryColor: '#0EA5E9',
    lat: null, lng: null, addressText: '',
    description: '', files: [],
    contactEmail: '', notifyOnUpdate: false,
  });

  const apiBase = process.env.NEXT_PUBLIC_API_URL || '/api';

  // Load categories
  useEffect(() => {
    fetch(`${apiBase}/public/categories`)
      .then(r => r.json())
      .then((data: Category[]) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, [apiBase]);

  const set = useCallback(<K extends keyof WizardState>(k: K, v: WizardState[K]) =>
    setState(s => ({ ...s, [k]: v })), []);

  // ── Validation ──────────────────────────────────────────────────────────────

  const canProceed = (): boolean => {
    if (step === 1) return !!state.categoryId;
    if (step === 2) return state.lat !== null && state.lng !== null;
    if (step === 3) return state.description.length >= 1;
    if (step === 4) return true; // contact optional
    return true;
  };

  const next = () => { if (canProceed()) setStep(s => (s < 5 ? (s + 1) as Step : s)); };
  const prev = () => setStep(s => (s > 1 ? (s - 1) as Step : s));

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('categoryId', state.categoryId);
      form.append('lat', String(state.lat));
      form.append('lng', String(state.lng));
      if (state.addressText) form.append('addressText', state.addressText);
      form.append('description', state.description);
      if (state.contactEmail) form.append('contactEmail', state.contactEmail);
      form.append('notifyOnUpdate', String(state.notifyOnUpdate));
      // Token Turnstile z widgetu (window.turnstile) gdy skonfigurowany; w dev
      // backend akceptuje dowolną wartość, gdy CAPTCHA_SECRET nie jest ustawione.
      const captchaToken = (window as any).__turnstileToken || 'dev-bypass';
      form.append('captchaToken', captchaToken);
      form.append('idempotencyKey', idempotencyKey.current);
      state.files.forEach(f => form.append('attachments', f));

      const res = await fetch(`${apiBase}/public/tickets`, { method: 'POST', body: form });
      const data = await res.json();
      if (!res.ok) { setError(data.message ?? 'Błąd wysyłania zgłoszenia'); return; }

      setTicketNumber(data.ticketNumber);
      setIsExternalManager(data.isExternalManager ?? false);
      setExternalManagerName(data.externalManagerName ?? null);
      setRoadClassLabel(data.roadClassLabel ?? null);
      setNotificationSent(data.notificationSent ?? false);
      setSubmitted(true);
    } catch {
      setError('Błąd sieci — spróbuj ponownie');
    } finally {
      setLoading(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────────────────────

  if (submitted) return (
    <SuccessScreen
      ticketNumber={ticketNumber}
      email={state.contactEmail}
      isExternalManager={isExternalManager}
      externalManagerName={externalManagerName}
      roadClassLabel={roadClassLabel}
      notificationSent={notificationSent}
    />
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-[#F8FAFC] to-white flex flex-col">
      {/* Header */}
      <header className="glass sticky top-0 z-40 border-b border-white/50 px-4 h-16 flex items-center gap-3">
        <a href="/zgloszenia" className="flex items-center gap-2.5">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/brand/logo/halogmina-horizontal.svg" alt="HaloGmina.pl" className="h-9 w-auto" />
        </a>
        <div className="ml-auto text-xs text-[#98A2B3] hidden sm:block">Zgłoszenie zajmie ~2 minuty</div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-8">
        <StepIndicator current={step} />

        <div className="bg-white rounded-2xl border border-[#E4E7EC] shadow-card-lg p-6 rise">

          {/* ── Step 1: Kategoria ─────────────────────────────────────────── */}
          {step === 1 && (
            <div>
              <h2 className="text-xl font-bold text-[#101828] mb-1">Co chcesz zgłosić?</h2>
              <p className="text-sm text-[#475467] mb-6">Wybierz kategorię problemu</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {categories.map(cat => {
                  const selected = state.categoryId === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => { set('categoryId', cat.id); set('categoryName', cat.name); set('categoryColor', cat.color); }}
                      className={`group relative p-4 rounded-2xl border-2 text-center transition-all hover:-translate-y-1 ${
                        selected
                          ? 'border-sky-500 bg-sky-50 shadow-md shadow-sky-500/10'
                          : 'border-[#E4E7EC] bg-white hover:border-sky-300 hover:bg-sky-50/50 hover:shadow-sm'
                      }`}
                    >
                      <div className={`w-12 h-12 mx-auto mb-2.5 rounded-xl flex items-center justify-center transition-colors ${
                        selected ? 'bg-gradient-to-br from-sky-500 to-indigo-500 text-white' : 'bg-sky-50 text-sky-600 group-hover:bg-sky-100'
                      }`}>
                        <CategoryIcon name={cat.icon} className="w-6 h-6" />
                      </div>
                      <p className="text-sm font-medium text-[#101828] leading-tight">{cat.name}</p>
                      {selected && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-sky-500 text-white flex items-center justify-center">
                          <IconCheck className="w-3 h-3" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Step 2: Lokalizacja ───────────────────────────────────────── */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-bold text-[#101828] mb-1">Gdzie jest problem?</h2>
              <p className="text-sm text-[#475467] mb-4">Kliknij na mapie lub przeciągnij pinezkę w dokładne miejsce</p>
              <MapPicker
                lat={state.lat}
                lng={state.lng}
                onPick={(lat, lng, addr) => { set('lat', lat); set('lng', lng); set('addressText', addr ?? ''); }}
              />
              {state.lat && (
                <div className="mt-3 p-3 bg-sky-50 border border-sky-200 rounded-lg text-sm text-[#0EA5E9]">
                  <strong>✓ Wskazana lokalizacja:</strong><br />
                  {state.addressText || `${state.lat.toFixed(6)}°N, ${state.lng!.toFixed(6)}°E`}
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Opis + zdjęcia ───────────────────────────────────── */}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-bold text-[#101828] mb-1">Opisz problem</h2>
              <p className="text-sm text-[#475467] mb-4">Im więcej szczegółów, tym szybciej urząd zareaguje</p>
              <textarea
                value={state.description}
                onChange={e => set('description', e.target.value)}
                placeholder={`np. "Duża dziura w nawierzchni po lewej stronie jezdni przy skrzyżowaniu z ul. Ogrodową..."`}
                rows={5}
                maxLength={2000}
                className="w-full px-4 py-3 text-sm border border-[#E4E7EC] rounded-xl resize-none focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-sky-100 text-[#101828] placeholder-[#98A2B3]"
              />
              <div className="flex justify-between text-xs mt-1">
                <span className={state.description.length < 20 ? 'text-[#98A2B3]' : 'text-emerald-600'}>
                  {state.description.length < 1 ? 'Wpisz opis problemu' : '✓ Opis wystarczający'}
                </span>
                <span className={`${state.description.length > 1800 ? 'text-amber-500' : 'text-[#98A2B3]'}`}>
                  {state.description.length}/2000
                </span>
              </div>

              <div className="mt-5">
                <p className="text-sm font-medium text-[#344054] mb-2">Dodaj zdjęcia (opcjonalnie, max 5)</p>
                <FileUploadGrid files={state.files} onChange={f => set('files', f)} />
              </div>
            </div>
          )}

          {/* ── Step 4: Kontakt ───────────────────────────────────────────── */}
          {step === 4 && (
            <div>
              <h2 className="text-xl font-bold text-[#101828] mb-1">Twoje dane (opcjonalne)</h2>
              <p className="text-sm text-[#475467] mb-6">Zostaw adres e-mail jeśli chcesz być informowany o postępach</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#344054] mb-1.5">E-mail do kontaktu</label>
                <div className="relative">
                  <IconMail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#98A2B3] pointer-events-none" />
                  <input
                    type="email"
                    value={state.contactEmail}
                    onChange={e => set('contactEmail', e.target.value)}
                    placeholder="jan.kowalski@gmail.com"
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-[#E4E7EC] rounded-xl focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-sky-100"
                  />
                </div>
              </div>
              <label className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                state.contactEmail ? 'hover:bg-sky-50' : 'opacity-50 cursor-not-allowed'
              }`}>
                <input
                  type="checkbox"
                  disabled={!state.contactEmail}
                  checked={state.notifyOnUpdate}
                  onChange={e => set('notifyOnUpdate', e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#0EA5E9]"
                />
                <div>
                  <p className="text-sm font-medium text-[#101828]">Chcę być informowany o zmianie statusu zgłoszenia</p>
                  <p className="text-xs text-[#475467] mt-0.5">Wyślemy Ci e-mail gdy status zgłoszenia się zmieni</p>
                </div>
              </label>
              <div className="mt-4 p-3.5 bg-sky-50/60 border border-sky-100 rounded-xl flex items-start gap-2.5 text-xs text-[#475467]">
                <IconLock className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" />
                <span>Twój e-mail jest poufny i nie będzie widoczny publicznie. Zgłoszenie jest anonimowe.</span>
              </div>
            </div>
          )}

          {/* ── Step 5: Podsumowanie ──────────────────────────────────────── */}
          {step === 5 && (
            <div>
              <h2 className="text-xl font-bold text-[#101828] mb-1">Sprawdź przed wysłaniem</h2>
              <p className="text-sm text-[#475467] mb-5">Upewnij się że dane są poprawne</p>
              <div className="bg-[#F8F9FB] rounded-xl border border-[#E4E7EC] divide-y divide-[#E4E7EC]">
                {[
                  { label: 'Kategoria', value: state.categoryName, step: 1 },
                  { label: 'Lokalizacja', value: state.addressText || `${state.lat?.toFixed(5)}°N, ${state.lng?.toFixed(5)}°E`, step: 2 },
                  { label: 'Opis', value: state.description.length > 100 ? state.description.slice(0, 100) + '...' : state.description, step: 3 },
                  { label: 'Zdjęcia', value: state.files.length ? `${state.files.length} ${state.files.length === 1 ? 'zdjęcie' : 'zdjęcia'}` : 'Brak', step: 3 },
                  { label: 'E-mail', value: state.contactEmail || 'Nie podano', step: 4 },
                  { label: 'Powiadomienia', value: state.notifyOnUpdate ? 'TAK' : 'NIE', step: 4 },
                ].map(({ label, value, step: s }) => (
                  <div key={label} className="flex items-start justify-between px-4 py-3 gap-3">
                    <span className="text-xs font-semibold text-[#475467] uppercase tracking-wide min-w-[90px]">{label}</span>
                    <span className="text-sm text-[#101828] flex-1 text-right">{value}</span>
                    <button onClick={() => setStep(s as Step)} className="text-xs text-[#0EA5E9] hover:underline whitespace-nowrap">[zmień]</button>
                  </div>
                ))}
              </div>

              {/* Captcha placeholder */}
              <div className="mt-5 p-4 bg-[#F8F9FB] rounded-xl border border-dashed border-[#E4E7EC] flex items-center justify-center gap-2 text-xs text-[#98A2B3]">
                <IconShield className="w-4 h-4" /> Ochrona antyspamowa (Turnstile) — konfigurowana w produkcji
              </div>

              {error && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>
              )}
            </div>
          )}
        </div>

        {/* ── Navigation ───────────────────────────────────────────────────── */}
        <div className="flex gap-3 mt-5">
          {step > 1 && (
            <button onClick={prev} className="inline-flex items-center gap-1.5 px-5 py-3 border border-[#E4E7EC] rounded-xl text-sm font-medium text-[#475467] hover:bg-white hover:border-gray-300 transition-colors">
              <IconArrowLeft className="w-4 h-4" /> Wstecz
            </button>
          )}
          <div className="flex-1" />
          {step < 5 ? (
            <button
              onClick={next}
              disabled={!canProceed()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-sky-500 to-indigo-500 text-white rounded-xl text-sm font-semibold hover:from-sky-600 hover:to-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:from-sky-500 disabled:hover:to-indigo-500 transition-all shadow-sm shadow-sky-500/30"
            >
              Dalej <IconArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="shimmer inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-sky-500 to-indigo-500 text-white rounded-xl text-base font-semibold hover:from-sky-600 hover:to-indigo-600 disabled:opacity-60 transition-all shadow-md shadow-sky-500/30"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Wysyłanie…</>
              ) : (
                <><IconSend className="w-5 h-5" /> Wyślij zgłoszenie</>
              )}
            </button>
          )}
        </div>
      </main>

      <footer className="text-center py-5 text-xs text-[#98A2B3]">
        © {new Date().getFullYear()} HaloGmina.pl
      </footer>
    </div>
  );
}

// ─── File Upload Grid ──────────────────────────────────────────────────────────

function FileUploadGrid({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const MAX = 5;
  const MAX_SIZE = 10 * 1024 * 1024;

  const addFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;
    const valid: File[] = [];
    Array.from(newFiles).forEach(f => {
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/heic'].includes(f.type)) return;
      if (f.size > MAX_SIZE) return;
      valid.push(f);
    });
    onChange([...files, ...valid].slice(0, MAX));
  };

  const remove = (i: number) => onChange(files.filter((_, idx) => idx !== i));

  return (
    <div className="grid grid-cols-5 gap-2">
      {files.map((f, i) => (
        <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-[#E4E7EC] group">
          <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
          <button
            onClick={() => remove(i)}
            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >×</button>
          <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate">{fmtSize(f.size)}</div>
        </div>
      ))}
      {files.length < MAX && (
        <button
          onClick={() => inputRef.current?.click()}
          className="aspect-square rounded-xl border-2 border-dashed border-[#E4E7EC] hover:border-[#0EA5E9] hover:bg-sky-50 flex flex-col items-center justify-center text-[#98A2B3] hover:text-[#0EA5E9] transition-all text-2xl"
        >
          +<span className="text-[9px] mt-0.5">dodaj</span>
        </button>
      )}
      <input ref={inputRef} type="file" multiple accept="image/*" className="hidden" onChange={e => addFiles(e.target.files)} />
    </div>
  );
}

// ─── Success Screen ────────────────────────────────────────────────────────────

function SuccessScreen({
  ticketNumber, email,
  isExternalManager, externalManagerName, roadClassLabel, notificationSent,
}: {
  ticketNumber: string;
  email: string;
  isExternalManager: boolean;
  externalManagerName: string | null;
  roadClassLabel: string | null;
  notificationSent: boolean;
}) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-[#E4E7EC] shadow-xl max-w-md w-full p-8 text-center">
        {/* Animated checkmark */}
        <svg className="w-20 h-20 mx-auto mb-5" viewBox="0 0 80 80" fill="none">
          <circle cx="40" cy="40" r="38" stroke="#10B981" strokeWidth="3" fill="#ECFDF5" />
          <path d="M22 40 L35 53 L58 27" stroke="#10B981" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
            style={{ strokeDasharray: 60, strokeDashoffset: 60, animation: 'draw-check 0.4s 0.4s ease-out forwards' }} />
          <style>{`@keyframes draw-check { to { stroke-dashoffset: 0; } }`}</style>
        </svg>

        <h1 className="text-2xl font-bold text-[#101828] mb-2">Zgłoszenie przyjęte!</h1>
        <p className="text-[#475467] text-sm mb-5">
          Twoje zgłoszenie zostało zarejestrowane w systemie.
        </p>

        {/* Numer zgłoszenia */}
        <div className="bg-[#F8F9FB] border border-[#E4E7EC] rounded-xl px-6 py-4 mb-5">
          <p className="text-xs text-[#475467] mb-1 uppercase tracking-wide font-medium">Numer zgłoszenia</p>
          <p className="text-2xl font-bold text-[#101828] font-mono tracking-widest">{ticketNumber}</p>
        </div>

        {/* Informacja o zarządcy zewnętrznym */}
        {isExternalManager ? (
          <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl text-left">
            <div className="flex items-start gap-2.5">
              <IconExternal className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-900">Zgłoszenie przekazane do zarządcy zewnętrznego</p>
                <p className="text-xs text-amber-700 mt-1">
                  Wskazana lokalizacja leży na{roadClassLabel ? ` ${roadClassLabel}` : ' drodze'},
                  która nie należy do zasobów gminy.
                  {externalManagerName && (
                    <> Zgłoszenie zostało przekazane do: <strong>{externalManagerName}</strong>.</>
                  )}
                </p>
                {notificationSent && (
                  <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1.5">
                    <IconMail className="w-3.5 h-3.5" /> Zarządca zewnętrzny został automatycznie powiadomiony.
                  </p>
                )}
                {!notificationSent && externalManagerName && (
                  <p className="text-xs text-amber-600 mt-1.5">
                    Urząd poinformuje właściwego zarządcę o Twoim zgłoszeniu.
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-xs text-[#98A2B3] mb-5">
            Urząd rozpatrzy Twoje zgłoszenie w ciągu kilku dni roboczych.
          </p>
        )}

        {/* E-mail potwierdzenie */}
        {email && (
          <p className="text-sm text-[#475467] mb-5">
            Potwierdzenie wysłaliśmy na adres:<br />
            <strong className="text-[#101828]">{email}</strong>
          </p>
        )}

        <div className="flex flex-col gap-2">
          <a
            href={`/status/${ticketNumber}`}
            className="block px-5 py-2.5 bg-[#0EA5E9] text-white rounded-xl text-sm font-semibold hover:bg-sky-600 transition-colors"
          >
            Sprawdź status zgłoszenia
          </a>
          <a
            href="/report"
            className="block px-5 py-2.5 border border-[#E4E7EC] text-[#475467] rounded-xl text-sm font-medium hover:bg-[#F8F9FB] transition-colors"
          >
            Zgłoś kolejny problem
          </a>
        </div>
      </div>
    </div>
  );
}
